"use client";

import { X } from "lucide-react";
import type * as React from "react";
import { Button } from "@/components/ui/atoms/button";
import {
  type McpRecommendedMcpServer,
  McpRecommendedToolkitsChip,
} from "@/components/ui/molecules/mcp-server-picker/recommended-toolkits-chip";
import {
  McpReviewCountChip,
  type McpReviewCountChipProps,
} from "@/components/ui/molecules/mcp-server-picker/review-count-chip";
import {
  McpToolkitChip,
  type McpToolkitChipProps,
} from "@/components/ui/molecules/mcp-server-picker/toolkit-chip";
import { cn } from "@/lib/utils";

export type McpSelectionSummaryMcpServer = {
  /** Stable React key — typically the MCP server name or id. */
  id: string;
} & Omit<McpToolkitChipProps, "onOpen" | "onRemove">;

export type McpSelectionSummaryBarProps = {
  /**
   * Selected MCP servers, in display order. An empty array renders the idle
   * placeholder; the bar keeps its height so the surrounding layout doesn't
   * shift on first selection.
   *
   * Servers in `recommendedMcpServers` are NOT also included here — the
   * consumer separates the two lists so this bar always renders the
   * grouped recommended chip first, with the rest of the per-server chips
   * trailing.
   */
  mcpServers: McpSelectionSummaryMcpServer[];
  /**
   * Recommended MCP servers collapsed into a single grouped chip. When this
   * array is non-empty the bar renders one "Recommended tools" chip in
   * front of the per-server chips; the chip's popover lists every entry.
   */
  recommendedMcpServers?: McpRecommendedMcpServer[];
  /**
   * Total selected tools across all MCP servers. Drives the count chip's
   * primary number.
   */
  toolCount: number;
  /**
   * Whether the grouped review is currently open — flips the count chip to
   * its pressed state so the user can see where the click landed.
   */
  reviewing?: boolean;
  /**
   * Whether the grouped review is currently filtered to recommended servers —
   * flips the recommended chip into its active ring state.
   */
  reviewingRecommended?: boolean;
  /** Custom aria-label for the count chip. */
  reviewLabel?: McpReviewCountChipProps["aria-label"];
  /** Open the grouped selected-tools view. */
  onReview?: () => void;
  /** Open the grouped review scoped to the recommended set. */
  onReviewRecommended?: () => void;
  /** Open this MCP server's tools to adjust selection. */
  onOpenMcpServer?: (id: string) => void;
  /** Remove this MCP server (and all of its selected tools). */
  onRemoveMcpServer?: (id: string) => void;
  /** Remove the entire recommended set in one go. */
  onRemoveRecommended?: () => void;
  /** Clear the entire selection. */
  onClear?: () => void;
  /** Copy override for the empty / no-selection-yet placeholder. */
  emptyMessage?: string;
} & Omit<React.ComponentProps<"div">, "children" | "onReview">;

/**
 * Persistent selection summary used inside the MCP picker shell: the total
 * tool count (clickable — opens the grouped selected-tools view), the
 * grouped recommended chip (if any), one removable chip per other selected
 * MCP server, and a clear-all on the right. Sits above the results so the
 * selection is obvious at a glance and easy to prune.
 *
 * Pure presentational — the consumer supplies the ordered server list and
 * the callbacks. Domain-specific ordering (e.g. "newest-selected first")
 * belongs in the consumer.
 */
export function McpSelectionSummaryBar({
  mcpServers,
  recommendedMcpServers = [],
  toolCount,
  reviewing = false,
  reviewingRecommended = false,
  reviewLabel,
  onReview,
  onReviewRecommended,
  onOpenMcpServer,
  onRemoveMcpServer,
  onRemoveRecommended,
  onClear,
  emptyMessage = "No tools selected yet",
  className,
  ...rest
}: McpSelectionSummaryBarProps) {
  const totalServers = mcpServers.length + recommendedMcpServers.length;
  const hasSelection = totalServers > 0;

  return (
    <div
      {...rest}
      className={cn(
        "@container flex min-h-9 items-center gap-2 rounded-lg bg-muted/30 px-2.5 py-1.5",
        className
      )}
      data-slot="mcp-selection-summary-bar"
      data-status={hasSelection ? "filled" : "empty"}
    >
      {hasSelection ? (
        <>
          <McpReviewCountChip
            active={reviewing}
            aria-label={reviewLabel}
            onClick={onReview}
            serverCount={totalServers}
            toolCount={toolCount}
          />
          {/* The per-server chip row gets crushed to a sliver in a narrow
              container; hide it there. The count chip still opens the grouped
              review and Clear all still works, so nothing is lost. */}
          <span
            aria-hidden
            className="@md:block hidden h-5 w-px shrink-0 bg-border"
          />
          <div className="scroll-fade-x @md:flex hidden min-w-0 flex-1 gap-1.5 overflow-x-auto p-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {recommendedMcpServers.length > 0 ? (
              <McpRecommendedToolkitsChip
                active={reviewingRecommended}
                mcpServers={recommendedMcpServers}
                onOpenMcpServer={onOpenMcpServer}
                onRemove={onRemoveRecommended}
                onReview={onReviewRecommended}
              />
            ) : null}
            {mcpServers.map(({ id, ...chip }) => (
              <McpToolkitChip
                key={id}
                {...chip}
                onOpen={onOpenMcpServer ? () => onOpenMcpServer(id) : undefined}
                onRemove={
                  onRemoveMcpServer ? () => onRemoveMcpServer(id) : undefined
                }
              />
            ))}
          </div>
          {onClear ? (
            <Button
              // A bordered, icon-led button that warms to a destructive tint on
              // hover — stands out from the neutral chips and reads as "remove
              // everything" without shouting at rest.
              className="ml-auto gap-1 hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive"
              onClick={onClear}
              size="sm"
              variant="outline"
            >
              <X aria-hidden />
              Clear all
            </Button>
          ) : null}
        </>
      ) : (
        <span className="flex shrink-0 items-center px-2 py-1 font-medium text-muted-foreground text-xs">
          {emptyMessage}
        </span>
      )}
    </div>
  );
}
